import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { getAuth } from '../../auth/core/AuthHelpers';
import { getInsUrl } from '../services';

export type SSEProgressState = {
	progress: number;
	status: 'idle' | 'connecting' | 'processing' | 'completed' | 'error' | 'waiting_action';
	message: string;
	jobId: string | null;
	// Thêm field cho RESULT event
	result?: SSEResultData | null;
};

export type SSEProgressEvent = {
	type: 'progress' | 'completed' | 'error';
	progress?: number;
	message?: string;
	status?: string;
};

/**
 * Data từ RESULT event (SIGNING_SUCCESSFUL / SIGNING_FAILED)
 * Format: RESULT|SIGNING_SUCCESSFUL|aggregateId;aggregateType|message
 *         RESULT|SIGNING_FAILED|aggregateId;aggregateType|message
 */
export type SSEResultData = {
	result: 'SIGNING_SUCCESSFUL' | 'SIGNING_FAILED' | string;
	aggregateId: string;
	aggregateType: string;
	message: string;
	raw: string; // Raw data gốc để debug
};

/**
 * Parsed INFO event
 * Format: INFO|message
 */
export type SSEInfoData = {
	message: string;
	raw: string;
};

type UseSSEProgressProps = {
	baseUrl?: string;
	progressEndpoint?: string;
	onCompleted?: (jobId: string, data?: any) => void;
	onError?: (error: string, jobId?: string) => void;
	/** Callback khi nhận được INFO event (vd: "Vui lòng xác thực trên mobile app") */
	onInfo?: (data: SSEInfoData) => void;
	/** Callback khi nhận được RESULT event (SIGNING_SUCCESSFUL / SIGNING_FAILED) */
	onResult?: (data: SSEResultData) => void;
	autoClose?: boolean;
};

// ─────────────────────────────────────────────────────────────
// Parser helpers
// ─────────────────────────────────────────────────────────────

/**
 * Parse một raw SSE data string theo pipe-delimited format
 * Trả về null nếu không match bất kỳ format nào đã biết
 *
 * Các format được hỗ trợ:
 *   INFO|<message>
 *   RESULT|<SIGNING_SUCCESSFUL|SIGNING_FAILED>|<aggregateId;aggregateType>|<message>
 *   <number>|<message>           ← progress dạng "50|Đang xử lý"
 *   JSON string                  ← { type, progress, message, status }
 */
type ParsedSSEData =
	| { kind: 'info'; data: SSEInfoData }
	| { kind: 'result'; data: SSEResultData }
	| { kind: 'progress'; progress: number; message: string }
	| { kind: 'json'; data: SSEProgressEvent }
	| { kind: 'unknown'; raw: string };

export function parseSSEData(raw: string): ParsedSSEData {
	const trimmed = raw.trim();

	// 1. Thử parse JSON trước
	if (trimmed.startsWith('{')) {
		try {
			const data: SSEProgressEvent = JSON.parse(trimmed);
			return { kind: 'json', data };
		} catch {
			// không phải JSON, tiếp tục
		}
	}

	const parts = trimmed.split('|');
	const type = parts[0]?.toUpperCase();

	// 2. INFO|message
	if (type === 'INFO' && parts.length >= 2) {
		return {
			kind: 'info',
			data: {
				message: parts.slice(1).join('|'), // join lại phòng message có chứa '|'
				raw: trimmed,
			},
		};
	}

	// 3. RESULT|SIGNING_SUCCESSFUL|aggregateId;aggregateType|message
	//    RESULT|SIGNING_FAILED|aggregateId;aggregateType|message
	if (type === 'RESULT' && parts.length >= 4) {
		const resultCode = parts[1];        // SIGNING_SUCCESSFUL / SIGNING_FAILED
		const aggregatePart = parts[2];     // aggregateId;aggregateType
		const message = parts.slice(3).join('|');

		const [aggregateId = '', aggregateType = ''] = aggregatePart.split(';');

		return {
			kind: 'result',
			data: {
				result: resultCode,
				aggregateId,
				aggregateType,
				message,
				raw: trimmed,
			},
		};
	}

	// 4. <number>|<message>  ← progress fallback
	if (parts.length >= 2 && !isNaN(Number(parts[0]))) {
		return {
			kind: 'progress',
			progress: parseInt(parts[0], 10),
			message: parts.slice(1).join('|'),
		};
	}

	return { kind: 'unknown', raw: trimmed };
}

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

/**
 * Hook để quản lý Server-Sent Events (SSE) cho việc theo dõi tiến trình.
 *
 * Hỗ trợ các event type từ BE:
 *  - `progress` event:  JSON { progress, message }  hoặc  "50|Đang xử lý"
 *  - `completed` event: JSON { message }
 *  - `error` event:     JSON { message }  (custom BE error, khác với onerror native)
 *  - `signature` event (hoặc bất kỳ custom event nào được cấu hình qua extraEvents):
 *      • INFO|<message>
 *      • RESULT|SIGNING_SUCCESSFUL|<aggregateId;aggregateType>|<message>
 *      • RESULT|SIGNING_FAILED|<aggregateId;aggregateType>|<message>
 */
export const useSSEProgress = ({
	baseUrl = getInsUrl(),
	progressEndpoint = '/xml-processor/progress',
	onCompleted,
	onError,
	onInfo,
	onResult,
	autoClose = true,
}: UseSSEProgressProps = {}) => {
	const [state, setState] = useState<SSEProgressState>({
		progress: 0,
		status: 'idle',
		message: '',
		jobId: null,
		result: null,
	});

	const eventSourceRef = useRef<EventSource | EventSourcePolyfill | null>(null);
	const isConnectedRef = useRef(false);
	const isCompletedRef = useRef(false);
	const onCompletedRef = useRef(onCompleted);
	const onErrorRef = useRef(onError);
	const onInfoRef = useRef(onInfo);
	const onResultRef = useRef(onResult);

	useEffect(() => {
		onCompletedRef.current = onCompleted;
		onErrorRef.current = onError;
		onInfoRef.current = onInfo;
		onResultRef.current = onResult;
	}, [onCompleted, onError, onInfo, onResult]);

	// ── disconnect ──────────────────────────────────────────────
	const disconnect = useCallback(() => {
		if (eventSourceRef.current) {
			eventSourceRef.current.close();
			eventSourceRef.current = null;
			isConnectedRef.current = false;
		}
	}, []);

	// ── handleSignatureEvent ────────────────────────────────────
	/**
	 * Xử lý event dạng pipe-delimited từ BE (INFO / RESULT / ...)
	 * Tách riêng để dễ test và tái sử dụng cho nhiều event name khác nhau
	 */
	const handlePipeEvent = useCallback(
		(raw: string, jobId: string, props?: any) => {
			console.log(raw);
			const parsed = parseSSEData(raw);

			switch (parsed.kind) {
				case 'info': {
					// Hiển thị thông báo chờ action (vd: xác thực trên mobile)
					setState((prev) => ({
						...prev,
						status: 'waiting_action',
						message: parsed.data.message,
					}));
					toast.info(parsed.data.message, { autoClose: 5000 });
					onInfoRef.current?.(parsed.data);
					break;
				}

				case 'result': {
					const { result, message } = parsed.data;
					const isSuccess = result === 'SIGNING_SUCCESSFUL';

					isCompletedRef.current = true;

					setState({
						progress: isSuccess ? 100 : 0,
						status: isSuccess ? 'completed' : 'error',
						message,
						jobId,
						result: parsed.data,
					});

					if (isSuccess) {
						toast.success(message);
						onCompletedRef.current?.(jobId, { ...props, ...parsed.data });
					} else {
						toast.error(message);
						onErrorRef.current?.(message, jobId);
					}

					onResultRef.current?.(parsed.data);

					if (autoClose) {
						setTimeout(() => disconnect(), 1000);
					}
					break;
				}

				// Fallback: progress dạng "50|message" gửi qua event không phải 'progress'
				case 'progress': {
					setState((prev) => ({
						...prev,
						status: 'processing',
						message: parsed.message || `Đang xử lý... ${parsed.progress}%`,
						progress: parsed.progress,
					}));
					break;
				}

				case 'unknown':
				default:
					console.warn('[SSE] Unknown pipe data format');
			}
		},
		[autoClose, disconnect],
	);

	// ── connect ─────────────────────────────────────────────────
	const connect = useCallback(
		(jobId: string, props?: any) => {
			if (isConnectedRef.current && eventSourceRef.current) {
				disconnect();
			}

			const auth = getAuth();
			const token = auth?.access_token;

			if (!token) {
				const error = 'Missing authentication token';
				setState({ progress: 0, status: 'error', message: error, jobId });
				onErrorRef.current?.(error, jobId);
				return;
			}

			const url = `${baseUrl}${progressEndpoint}/${jobId}`;

			try {
				isCompletedRef.current = false; // Reset mỗi lần connect mới

				setState({
					progress: 0,
					status: 'connecting',
					message: 'Đang kết nối...',
					jobId,
					result: null,
				});

				const eventSource = new EventSourcePolyfill(url, {
					headers: { Authorization: `Bearer ${token}` },
					heartbeatTimeout: 5 * 60 * 1000,
				});
				eventSourceRef.current = eventSource;
				isConnectedRef.current = true;

				// ── onopen ──
				eventSource.onopen = () => {
					setState((prev) => ({
						...prev,
						status: 'processing',
						message: 'Bắt đầu xử lý...',
					}));
				};

				// ── progress event ──
				eventSource.addEventListener('progress', (event: Event | MessageEvent) => {
					try {
						if (!('data' in event)) return;
						const parsed = parseSSEData(event.data);

						let currentProgress = 0;
						let message = '';

						if (parsed.kind === 'json') {
							currentProgress = parsed.data.progress || 0;
							message = parsed.data.message || '';
						} else if (parsed.kind === 'progress') {
							currentProgress = parsed.progress;
							message = parsed.message;
						} else {
							// Fallback cho INFO / RESULT gửi nhầm qua event 'progress'
							handlePipeEvent(event.data, jobId, props);
							return;
						}

						if (currentProgress >= 100) {
							isCompletedRef.current = true;
							setState({ progress: 100, status: 'completed', message: message || 'Hoàn thành!', jobId });
							onCompletedRef.current?.(jobId, { ...props, ...parsed });
							if (autoClose) setTimeout(() => disconnect(), 1000);
						} else {
							setState((prev) => ({
								...prev,
								progress: currentProgress,
								status: 'processing',
								message: message || `Đang xử lý... ${currentProgress}%`,
							}));
						}
					} catch (err) {
						console.error('[SSE] Error parsing progress event:', err);
					}
				});

				// ── completed event ──
				eventSource.addEventListener('completed', (event: Event | MessageEvent) => {
					try {
						if (!('data' in event)) return;
						const data: SSEProgressEvent = JSON.parse(event.data);

						isCompletedRef.current = true;
						setState({ progress: 100, status: 'completed', message: data.message || 'Hoàn thành!', jobId });
						onCompletedRef.current?.(jobId, { ...props, ...data });
						if (autoClose) setTimeout(() => disconnect(), 1000);
					} catch (err) {
						console.error('[SSE] Error parsing completed event:', err);
					}
				});

				// ── signature event  ← NEW ──
				// BE gửi event name là 'signature' với data dạng pipe-delimited
				// vd: INFO|Vui lòng xác thực...
				//     RESULT|SIGNING_SUCCESSFUL|aggId;aggType|message
				//     RESULT|SIGNING_FAILED|aggId;aggType|message
				eventSource.addEventListener('signature', (event: Event | MessageEvent) => {
					if (!('data' in event)) return;
					handlePipeEvent((event as MessageEvent).data, jobId, props);
				});

				// ── custom error event (BE gửi, có data) ──
				eventSource.addEventListener('error', (event: Event | MessageEvent) => {
					if (!('data' in event)) return; // native error → để onerror xử lý

					try {
						const data: SSEProgressEvent = JSON.parse((event as MessageEvent).data);
						const errorMessage = data.message || 'Có lỗi xảy ra trong quá trình xử lý';

						setState((prev) => {
							if (prev.status === 'completed') return prev;
							return { ...prev, status: 'error', message: errorMessage };
						});

						toast.error(errorMessage);
						onErrorRef.current?.(errorMessage, jobId);
						disconnect();
					} catch (err) {
						console.error('[SSE] Error parsing custom error event:', err);
					}
				});

				// ── native onerror ──
				eventSource.onerror = () => {
					if (eventSource.readyState === 0) {
						if (isCompletedRef.current) {
							disconnect();
							return;
						}
						const errorMessage = 'Lỗi kết nối SSE';
						setState((prev) => {
							if (prev.status === 'completed') return prev;
							toast.error(errorMessage);
							onErrorRef.current?.(errorMessage, jobId);
							return { ...prev, status: 'error', message: errorMessage };
						});
						disconnect();
					} else if (eventSource.readyState === 2) {
						disconnect();
					}
				};
			} catch (error: any) {
				const errorMessage = error.message || 'Không thể kết nối SSE';
				setState({ progress: 0, status: 'error', message: errorMessage, jobId });
				toast.error(errorMessage);
				onErrorRef.current?.(errorMessage, jobId);
				isConnectedRef.current = false;
			}
		},
		[baseUrl, progressEndpoint, autoClose, disconnect, handlePipeEvent],
	);

	// ── reset ───────────────────────────────────────────────────
	const reset = useCallback(() => {
		disconnect();
		setState({ progress: 0, status: 'idle', message: '', jobId: null, result: null });
	}, [disconnect]);

	// ── cleanup on unmount ──────────────────────────────────────
	useEffect(() => () => disconnect(), [disconnect]);

	return {
		state,
		connect,
		disconnect,
		reset,
		isConnected: isConnectedRef.current,
	};
};